import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useMenu } from "@/menu/MenuApp";
import { loc } from "@/menu/i18n";

export default function DishPage() {
  const { id } = useParams();
  const { data, lang, t } = useMenu();
  const dish = data.dishes.find((d) => d.id === id);
  if (!dish) return <div className="px-6 py-20 text-center text-[#A3A3A3]">—</div>;
  const cat = data.categories.find((c) => c.id === dish.category_id);
  const catSlug = cat ? cat.slug || cat.id.replace("cat-", "") : "";

  return (
    <div data-testid="menu-dish-page">
      <div className="relative h-72 overflow-hidden sm:h-96">
        <motion.img
          src={dish.image} alt={loc(dish.name, lang)}
          initial={{ scale: 1.08, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-black/30 to-black/20" />
      </div>

      <div className="mx-auto max-w-3xl px-5 pb-16">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}>
          <Link to={`/menu/categoria/${catSlug}`} data-testid="dish-back-link" className="mt-7 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#A3A3A3] transition-colors hover:text-[#E5CAA4]">
            <ArrowLeft size={14} /> {cat ? loc(cat.name, lang) : t("backToMenu")}
          </Link>
          <div className="mt-5 flex items-baseline justify-between gap-4">
            <h1 className="font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl" data-testid="dish-name">{loc(dish.name, lang)}</h1>
            <span className="font-display shrink-0 text-3xl italic text-[#C19D60]" data-testid="dish-price">{Number.isInteger(dish.price) ? dish.price : dish.price.toFixed(1)}€</span>
          </div>
          <span className="mt-4 block h-px w-16 bg-[#C19D60]/60" />
          {!dish.available && <p className="mt-5 text-[11px] font-medium uppercase tracking-[0.2em] text-red-400/80">{t("unavailable")}</p>}
          <p className="mt-6 text-base font-light leading-relaxed text-[#c9c9c9]" data-testid="dish-description">{loc(dish.description, lang)}</p>

          {dish.allergens?.length > 0 && (
            <div className="mt-10" data-testid="dish-allergens">
              <p className="mb-4 text-[10px] uppercase tracking-[0.3em] text-[#C19D60]">{t("allergens")}</p>
              <div className="space-y-px overflow-hidden rounded-lg border border-white/5">
                {dish.allergens.map((code) => {
                  const a = data.allergens.find((x) => x.code === code);
                  if (!a) return null;
                  return (
                    <div key={code} className="flex items-start gap-4 bg-[#101012] p-4">
                      <span className="font-display mt-0.5 w-7 shrink-0 text-lg font-light italic text-[#C19D60]/70">{String(code).padStart(2, "0")}</span>
                      <div>
                        <p className="text-sm font-medium">{lang === "en" ? a.en : a[`name_${lang}`] || a[lang] || a.it}</p>
                        <p className="mt-0.5 text-xs font-light text-[#A3A3A3]">{lang === "en" ? a.desc_en : a[`desc_${lang}`] || a.desc_it}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
